import { Component, EventEmitter, Inject, OnDestroy, OnInit, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { filter, finalize, switchMap, tap, catchError, Subscription, of, BehaviorSubject, take } from 'rxjs';
import moment from 'moment-timezone';
import { parseNumber, formatNumber, ParsedNumber } from 'libphonenumber-js';
import { CookieService } from 'ngx-cookie-service';

import { COUNTRIES } from '@shared/core/constants';
import { INDUSTRIES, FORBIDDEN_NAMES } from '@shared/restaurant/constants';
import {
  ConfirmationModalRef,
  ConfirmationModalService,
  forbiddenNamesValidator,
  ValidationService,
  USERNAME_PATTERN,
  ACCOUNT_LOCATION_NAME_PATTERN,
  emailRegexp,
  phoneRegexp,
  NAME_PATTERN,
} from '@tablesready/common-ui';
import { AuthStatesEnum } from '../../constants';
import { LoginService } from '../../services';
import { AppIcon } from '@tablesready/frontend-icons';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { AuthService } from '@tablesready/frontend-auth';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';

@Component({
  selector: 'tablesready-signup',
  styleUrls: ['./signup.component.scss'],
  templateUrl: './signup.component.html',
})
export class SignupComponent implements OnInit, OnDestroy {
  @Input() planCode: string;
  @Output() onToggle = new EventEmitter<AuthStatesEnum>();

  icon = AppIcon;
  form: UntypedFormGroup;
  submitting = false;
  authStates = AuthStatesEnum;
  step: number = 0;
  passwordType = 'password';
  countryOptions = COUNTRIES;
  industries = INDUSTRIES;
  brand: string;
  gaCookieValue: string;
  gaClientId: string;
  emailValid$ = new BehaviorSubject<boolean>(false);
  usernameValid$ = new BehaviorSubject<boolean>(false);
  private confirmationRef?: ConfirmationModalRef<boolean>;
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly router: Router,
    private readonly validationService: ValidationService,
    private readonly confirmationModalService: ConfirmationModalService,
    private readonly authService: AuthService,
    private readonly alertsService: AlertsService,
    private readonly loginService: LoginService,
    private readonly cookieService: CookieService,
    @Inject(APP_VARIABLES_TOKEN) public readonly appVariables: AppVariables,
  ) {
  }

  onBlur(): void {
    this.emailValid$.next(this.form.get('email').valid);
    this.usernameValid$.next(this.form.get('username').valid);
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      fullName: ['', [
        Validators.required,
        Validators.maxLength(50),
        Validators.pattern(NAME_PATTERN),
      ]],
      email: [
        '',
        [Validators.required, Validators.pattern(emailRegexp), Validators.maxLength(100)],
        [
          (control: AbstractControl) => this.validationService.emailUsedValidator(control.value),
          (control: AbstractControl) => this.validationService.validateEmail(control.value)
        ],
      ],
      username: [
        '',
        [Validators.required, Validators.maxLength(50), Validators.pattern(USERNAME_PATTERN)],
        [(control: AbstractControl) => this.validationService.usernameUsedValidator(control.value)]
      ],
      newPassword: ['', [
        Validators.required,
        Validators.minLength(4),
        Validators.maxLength(100),
        this.validationService.passwordValidator(),
      ]],
      name: ['', [
        Validators.required,
        Validators.maxLength(50),
        Validators.pattern(ACCOUNT_LOCATION_NAME_PATTERN),
        forbiddenNamesValidator(FORBIDDEN_NAMES),
      ]],
      phoneNumber: ['', [
        Validators.required,
        Validators.pattern(phoneRegexp),
        this.validationService.phonePatternCountryValidator(this.countryOptions[0].abbr),
      ]],
      country: [this.countryOptions[0], [Validators.required]],
    });
    this.form.addControl('industry', new UntypedFormControl(null, [Validators.required]));

    const changeCountrySubscription = this.form.controls.country.valueChanges
      .subscribe(country => {
        this.form.controls.phoneNumber.setValidators([
          Validators.required,
          Validators.pattern(phoneRegexp),
          this.validationService.phonePatternCountryValidator(country.abbr),
        ]);
        this.form.controls.phoneNumber.updateValueAndValidity();
      });
    this.subscriptions.add(changeCountrySubscription);

    this.gaCookieValue = this.cookieService.get('_ga') || '';
    this.gaClientId = this.gaCookieValue.substring(6);
  }

  ngOnDestroy(): void {
    this.onToggle.complete();
    if (this.confirmationRef) {
      this.confirmationRef.dismiss();
    }
  }

  signup() {
    const data = {
      fullName: this.form.value.fullName,
      username: this.form.value.username.toLowerCase(),
      name: this.form.value.name,
      email: this.form.value.email.toLowerCase(),
      phoneNumber: formatNumber(
        parseNumber(this.form.value.phoneNumber, this.form.value.country.abbr) as ParsedNumber,
        'E.164'
      ),
      domain: 'tablesready',
      planCode: this.planCode,
      newPassword: this.form.value.newPassword,
      country: this.form.value.country.abbr,
      timezone: moment.tz.guess(),
      industry: this.form.value.industry,
      gaClientId: this.gaClientId,
    };

    this.subscriptions.add(
      this.authService.signup(data)
        .pipe(
          filter((_data: any) => _data.success),
          switchMap(() => this.loginService.login({
            username: data.username,
            password: data.newPassword,
          })),
          tap(() => this.router.navigateByUrl('/waitlist')),
          finalize(() => {
            this.submitting = false;
            // location.reload();
          }),
          catchError(error => {
            this.alertsService.add({
              type: IAlertTypes.danger,
              heading: 'Error signing up.',
              body: error,
            });

            return of(null);
          })
        )
        .subscribe()
    );
  }

  goToLogin(): void {
    this.onToggle.emit(this.authStates.login);
  }

  get fullNameControl(): AbstractControl {
    return this.form.get('fullName');
  }

  get emailControl(): AbstractControl {
    return this.form.get('email');
  }

  get usernameControl(): AbstractControl {
    return this.form.get('username');
  }

  get newPasswordControl(): AbstractControl {
    return this.form.get('newPassword');
  }

  get nameControl(): AbstractControl {
    return this.form.get('name');
  }

  get phoneNumberControl(): AbstractControl {
    return this.form.get('phoneNumber');
  }

  get countryControl(): AbstractControl {
    return this.form.get('country');
  }

  get industryControl(): AbstractControl {
    return this.form.get('industry');
  }
}
